import React, { FunctionComponent, Suspense, useState } from 'react';
import { graphql } from 'react-relay';
import { useLazyLoadQuery } from 'react-relay/hooks';
import { CatalogListLoader } from 'src/components/widgets/catalogList';
import { CustomErrorBoundary } from 'src/components/widgets/customErrorBoundary';
import { Section } from 'src/components/widgets/section';
import { ErrorMessage } from 'src/shared/globals';
import { T } from 'src/shared/helpers';
import { RelayFragment } from 'src/shared/types';
import { Caption } from '../../../widgets/headers';
import { FormControl } from '../../../widgets/formControl';
import { PageHeader } from '../../../widgets/pageHeader';
import { SearchInput } from '../../../widgets/input';
import { VerticalLayout } from '../../../widgets/layouts';
import { merchantProduct_item$data } from '../state/queries/__generated__/merchantProduct_item.graphql';
import { storeCatalogQuery } from './__generated__/storeCatalogQuery.graphql';
import StoreCatalogPaginationFragment from './storeCatalogPaginationFragment';
import styled from 'styled-components';

const CaptionContainer = styled.div`
    margin-top: 4px;
`;

// Parent page
const StoreCatalog: FunctionComponent<{
    salesChannelID: string;
    scrollContainer?: HTMLDivElement | null;
    selectedProductsSKUs?: string[];
    cardTitle?: string;
    cardTitleHtml?: React.ReactNode;
    onProductClick: (product: Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>) => void;
    showHeader?: boolean;
    showBackButton?: boolean;
    hideAlreadyConfiguredBadge?: boolean;
    hideProductsNotConfigured?: boolean;
    disableProductCode?: string;
    searchProduct?: string;
    isAddSalesChannel?: boolean;
    isPublishingProduct?: boolean;
    isForImportingVariants?: boolean;
}> = ({
    scrollContainer,
    selectedProductsSKUs,
    onProductClick,
    salesChannelID,
    cardTitle,
    cardTitleHtml,
    showHeader,
    showBackButton,
    hideAlreadyConfiguredBadge = false,
    hideProductsNotConfigured = false,
    disableProductCode,
    searchProduct = '',
    isAddSalesChannel,
    isPublishingProduct,
    isForImportingVariants
}) => {
    const [search, setSearch] = useState('');

    const handleSearch = (search: string) => {
        setSearch(search);
    };

    return (
        <VerticalLayout>
            {/*<PageHeader isBackVisible={ showBackButton } title={T._('Choose a product', 'Admin')} />*/}
            <Section
                description={
                    cardTitleHtml ? (
                        cardTitleHtml
                    ) : (
                        <span>{cardTitle ?? T._('Choose the product from your store that you wish to make customizable via Zakeke', 'Admin')}</span>
                    )
                }
            >
                <CustomErrorBoundary render={() => <ErrorMessage>{T._('Failed to get the products list from your store.', 'Admin')}</ErrorMessage>}>
                    {!isAddSalesChannel && !isPublishingProduct && !isForImportingVariants && (
                        <FormControl>
                            <SearchInput fullWidth placeholder={T._('Search by product name or SKU...', 'Admin')} onSearch={handleSearch} />
                            <CaptionContainer>
                                <Caption>
                                    {T._(
                                        'Search by product name or ID. On Etsy you can also search by listing ID or phrase with related terms, and on Magento you can search by SKU.',
                                        'Admin'
                                    )}
                                </Caption>
                            </CaptionContainer>
                        </FormControl>
                    )}

                    <Suspense fallback={CatalogListLoader}>
                        <StoreCatalogQuery
                            salesChannelID={salesChannelID}
                            search={searchProduct || search}
                            onProductClick={onProductClick}
                            scrollContainer={scrollContainer}
                            selectedProductsSKUs={selectedProductsSKUs}
                            hideProductsNotConfigured={hideProductsNotConfigured}
                            hideAlreadyConfiguredBadge={hideAlreadyConfiguredBadge}
                            disableProductCode={disableProductCode}
                        />
                    </Suspense>
                </CustomErrorBoundary>
            </Section>
        </VerticalLayout>
    );
};

// Container for the query
const StoreCatalogQuery: FunctionComponent<{
    salesChannelID: string;
    search: string;
    scrollContainer?: HTMLDivElement | null;
    selectedProductsSKUs?: string[];
    onProductClick: (merchantProduct: Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>) => void;
    hideAlreadyConfiguredBadge?: boolean;
    hideProductsNotConfigured?: boolean;
    disableProductCode?: string;
}> = ({
    salesChannelID,
    search,
    scrollContainer,
    selectedProductsSKUs,
    onProductClick,
    hideAlreadyConfiguredBadge,
    hideProductsNotConfigured,
    disableProductCode
}) => {
    const query = useLazyLoadQuery<storeCatalogQuery>(
        graphql`
            query storeCatalogQuery($salesChannelID: ID!) {
                ...storeCatalogPaginationFragment__merchantProducts @arguments(salesChannelID: $salesChannelID)
            }
        `,
        {
            salesChannelID
        }
    );

    return (
        <StoreCatalogPaginationFragment
            query={query}
            search={search}
            selectedProductsSKUs={selectedProductsSKUs}
            onProductClick={onProductClick}
            scrollContainer={scrollContainer}
            hideProductsNotConfigured={hideProductsNotConfigured}
            hideAlreadyConfiguredBadge={hideAlreadyConfiguredBadge}
            disableProductCode={disableProductCode}
        />
    );
};

export default StoreCatalog;
